const SENTRY_MASK = '[Filtered]';

const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'proxy-authorization',
  'set-cookie',
  'x-csrf-token',
  'x-xsrf-token',
]);

const SENSITIVE_KEY_PATTERNS = [
  'access_token',
  'api_key',
  'api-key',
  'authorization',
  'card_number',
  'client_secret',
  'credit_card',
  'current_password',
  'cvv',
  'cvc',
  'date_of_birth',
  'dob',
  'e_mail',
  'email',
  'otp',
  'passcode',
  'password',
  'password_confirmation',
  'phone',
  'phone_number',
  'pin',
  'private_key',
  'refresh_token',
  'secret',
  'ssn',
  'token',
  'address',
  'contact_email',
  'ip_address',
  'mobile',
  'national_id',
  'passport',
  'user_email',
];

export const sentryString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return fallback;
};

export const sentryBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    if (['1', 'true', 'yes', 'on'].includes(value.toLowerCase())) {
      return true;
    }

    if (['0', 'false', 'no', 'off'].includes(value.toLowerCase())) {
      return false;
    }
  }

  return fallback;
};

export const sentryNumber = (
  value: unknown,
  fallback: number | undefined = undefined,
): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallback;
};

export const sentryList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => sentryString(item)).filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

export const sanitizeSentryEvent = (
  event: Record<string, any>,
  sendPii = false,
): Record<string, any> => {
  if (event.request) {
    event.request = sanitizeSentryRequest(event.request);
  }

  if (event.extra) {
    event.extra = sanitizeSentryValue('extra', event.extra);
  }

  if (event.contexts) {
    event.contexts = sanitizeSentryValue('contexts', event.contexts);
  }

  if (!sendPii && event.user) {
    event.user = event.user.id ? { id: event.user.id } : undefined;
  }

  return event;
};

export const sanitizeSentryBreadcrumb = (
  breadcrumb: Record<string, any>,
): Record<string, any> => {
  if (breadcrumb.data) {
    breadcrumb.data = sanitizeSentryValue('data', breadcrumb.data);
  }

  if (breadcrumb.message) {
    breadcrumb.message = sanitizeSentryUrl(String(breadcrumb.message));
  }

  return breadcrumb;
};

export const sanitizeSentryRequest = (
  request: Record<string, any>,
): Record<string, any> => {
  if (request.headers) {
    request.headers = sanitizeSentryHeaders(request.headers);
  }

  delete request.cookies;

  if (typeof request.url === 'string') {
    request.url = sanitizeSentryUrl(request.url);
  }

  if (typeof request.query_string === 'string') {
    request.query_string = sanitizeSentryQueryString(request.query_string);
  }

  for (const key of ['data', 'env', 'query_data']) {
    if (key in request) {
      request[key] = sanitizeSentryValue(key, request[key]);
    }
  }

  return sanitizeSentryValue('request', request);
};

export const sanitizeSentryHeaders = (
  headers: HeadersInit | Record<string, any>,
): Record<string, any> => {
  const clean: Record<string, any> = {};
  const entries =
    typeof Headers !== 'undefined' && headers instanceof Headers
      ? Array.from(headers.entries())
      : Array.isArray(headers)
        ? headers
        : Object.entries(headers ?? {});

  for (const [key, value] of entries) {
    const normalized = key.toLowerCase().replace(/_/g, '-');

    if (SENSITIVE_HEADERS.has(normalized)) {
      continue;
    }

    clean[key] = sanitizeSentryValue(key, value);
  }

  return clean;
};

export const sanitizeSentryValue = (key: string, value: any): any => {
  if (isSensitiveSentryKey(key)) {
    return SENTRY_MASK;
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeSentryValue(key, item));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([childKey, childValue]) => [
        childKey,
        sanitizeSentryValue(childKey, childValue),
      ]),
    );
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return JSON.stringify(sanitizeSentryValue(key, JSON.parse(value)));
      } catch {
        return sanitizeSentryUrl(value);
      }
    }

    return sanitizeSentryUrl(value);
  }

  return value;
};

export const sanitizeSentryUrl = (value: string): string => {
  if (!/[?&]/.test(value)) {
    return value;
  }

  try {
    const isRelative = value.startsWith('/');
    const url = new URL(value, 'http://sentry.local');

    for (const key of Array.from(url.searchParams.keys())) {
      if (isSensitiveSentryKey(key)) {
        url.searchParams.set(key, SENTRY_MASK);
      }
    }

    if (isRelative) {
      return `${url.pathname}${url.search}${url.hash}`;
    }

    return url.toString();
  } catch {
    const [path, query = ''] = value.split('?');

    return query === ''
      ? value
      : `${path}?${sanitizeSentryQueryString(query)}`;
  }
};

const sanitizeSentryQueryString = (queryString: string): string => {
  const params = new URLSearchParams(queryString);

  for (const key of Array.from(params.keys())) {
    if (isSensitiveSentryKey(key)) {
      params.set(key, SENTRY_MASK);
    }
  }

  return params.toString();
};

const isSensitiveSentryKey = (key: string): boolean => {
  const normalized = key.toLowerCase().replace(/[-\s.]/g, '_');

  return SENSITIVE_KEY_PATTERNS.some((pattern) =>
    normalized.includes(pattern.replace(/[-\s.]/g, '_')),
  );
};
